import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  Icon,
  Text,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { WEEK_VIEW_ITEM_HEIGHT } from '~/lib/constants/calendar.constants';
import { getSlotOffsetInPx } from '~/lib/util/schedule/weekViewLayout.util';

const TOP_LEVEL_ID = 'week-cancelled-group-item';

export const getCancelledGroupLabel = (count: number): string =>
  `${count} cancellation${count == 1 ? '' : 's'}`;

interface ScheduleWeekCancelledGroupItemProps {
  cancelledItems: ScheduleItemBase[];
  leftPercent: number;
  onCancelledGroupClicked?: (startDateTime: string) => void;
  width: number;
}

/** Single card standing in for several cancelled events that start in the same half hour slot */
const ScheduleWeekCancelledGroupItem = ({
  cancelledItems,
  leftPercent,
  onCancelledGroupClicked,
  width,
}: ScheduleWeekCancelledGroupItemProps) => {
  // The card spans from the earliest start to the latest end of the events it replaces
  const start = DateTime.min(
    ...cancelledItems.map((item) => DateTime.fromISO(item.startDateTime)),
  );
  const end = DateTime.max(
    ...cancelledItems.map((item) => DateTime.fromISO(item.endDateTime)),
  );
  const startIso = start.toISO();
  const durationInMinutes = end.diff(start, 'minutes').minutes;

  const handleCancelledGroupClick = () => {
    onCancelledGroupClicked?.(startIso);
  };

  return (
    <ChakraBox
      _hover={{
        backgroundColor: 'critical.background.hover',
        cursor: 'pointer',
      }}
      alignItems="center"
      background="critical.background"
      border="1px solid"
      borderColor="critical.text"
      borderRadius="4px"
      gap="4px"
      height={`${(durationInMinutes / 30) * WEEK_VIEW_ITEM_HEIGHT}px`}
      id={`${TOP_LEVEL_ID}__${startIso}`}
      left={`${leftPercent}%`}
      marginTop={`${getSlotOffsetInPx(startIso, WEEK_VIEW_ITEM_HEIGHT)}px`}
      overflow="hidden"
      padding="4px 8px"
      position="absolute"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
      width={`${width}%`}
      zIndex={1}
      onClick={handleCancelledGroupClick}
    >
      <ChakraFlex alignItems="center" gap="4px">
        <Icon color="critical.text" name={FAIconName.calendarXmarkRegular} />
        <Text
          id={`${TOP_LEVEL_ID}__${startIso}--label`}
          variant="captionMedium"
        >
          {getCancelledGroupLabel(cancelledItems.length)}
        </Text>
      </ChakraFlex>
      <Text variant="caption">See all</Text>
    </ChakraBox>
  );
};

export default ScheduleWeekCancelledGroupItem;
